import React from 'react';

import {
  Box,
  Button,
  Checkbox,
  Divider,
  Group,
  Input,
  LoadingOverlay,
  Select,
  Stack,
  Text,
  Textarea,
  TextInput,
  Title,
} from '@mantine/core';
import { DateInput } from '@mantine/dates';
import { useForm } from '@mantine/form';
import { useDisclosure, useShallowEffect } from '@mantine/hooks';
import Drawer from 'components/Drawer';
import ExpandableSection from 'components/ExpandableSection';
import RegionSelect from 'components/form-fields/auto-complete-fields/RegionSelect';
import PostalCodeInput from 'components/form-fields/input-fields/PostalCodeInput';
import CountrySelect from 'components/form-fields/select-fields/CountrySelect';
import HelpButton from 'components/HelpButton';
import ImageUploader from 'components/ImageUploader';
import InfoCard from 'components/InfoCard';
import InfoCardSection from 'components/InfoCard/InfoCardSection';
import { useAuth } from 'context/auth-context';
import { useLocalization } from 'context/localization-context';
import dayjs from 'dayjs';
import useCreateStaff from 'hooks/staff/useCreateStaff';
import useStaff from 'hooks/staff/useStaff';
import useUpdateStaff from 'hooks/staff/useUpdateStaff';
import { PhoneInput } from 'react-international-phone';
import 'react-international-phone/style.css';
import { StaffWrite } from 'types';
import { Country, CountryEnum } from 'types/enums';
import { emailValidator, nameValidator, phoneValidator } from 'utils/form-validators';
import { formatPhone, phoneInputCountries } from 'utils/formatters';
import { generateDemoGymFakeEmail } from 'utils/functions';
import notificationHandler from 'utils/notifications';
import { isWorkoutTrackerEnabled } from 'utils/workoutTracker';
import { v4 as uuidv4 } from 'uuid';

import CheckInProgramPermissionSection from './components/CheckInProgramPermissionSection';
import PermissionContent from './components/PermissionContent';

export interface DrawerStaffFormValues {
  staffId: string;
  email: string;
  firstName: string;
  lastName: string;
  phone: string;
  dob?: Date | null;
  isCoach?: boolean;
  gender?:
    | 'Male'
    | 'Female'
    | 'Transgender'
    | 'Non-binary/Non-conforming'
    | 'Prefer not to respond'
    | ''
    | null;
  address: {
    addressLines: string | null;
    locality: string | null;
    region: string | null;
    zipCode: string | null;
    country: Country;
  };
  medicalNotes?: string | null;
  emergencyName?: string | null;
  emergencyPhone: string;
  permissions: StaffWrite['permissions'];
}

interface IDrawerStaffFormProps {
  formId: string;
  title: JSX.Element;
  opened: boolean;
  closeHandler: () => void;
  staffId?: string;
  type: 'create' | 'edit';
  permission?: boolean;
}

const DEFAULTPERMISSIONS: StaffWrite['permissions'] = [
  'EventMutate',
  'InvoiceCreate',
  'AthleteView',
  'InvoiceAthleteView',
  'Kiosk',
  'PayrollReportViewOwn',
  'CheckInProgramView',
];

type Permission = StaffWrite['permissions'][number];

const DrawerStaffForm = ({
  formId,
  title,
  opened,
  closeHandler,
  staffId,
  type,
  permission = true,
}: IDrawerStaffFormProps): JSX.Element => {
  const { session } = useAuth();
  const { dateFormat } = useLocalization();

  const isDemoGym = !!session?.demoGym;
  const workoutTrackerEnabled = isWorkoutTrackerEnabled(session?.gym?.workoutTrackerEnabled);

  const { data, isLoading, isFetching, isSuccess } = useStaff(staffId);

  const [medicalOpened, { toggle: handleMedicalToggle, open: openMedical }] = useDisclosure();
  const [permissionsOpened, { toggle: handlePermissionsToggle }] = useDisclosure();

  const canChangePassword = data?.data.canChangeEmail || false;

  const { successNotification, errorNotification } = notificationHandler();

  const createMutation = useCreateStaff();
  const updateMutation = useUpdateStaff();

  const form = useForm<DrawerStaffFormValues>({
    initialValues: {
      staffId: data?.data.staffId || '',
      email: data?.data.email || (isDemoGym && type === 'create' ? generateDemoGymFakeEmail() : ''),
      firstName: data?.data.firstName || '',
      lastName: data?.data.lastName || '',
      phone: data?.data.phone || '',
      dob: null,
      isCoach: data?.data.isCoach || false,
      gender: data?.data.gender || null,
      address: {
        addressLines: data?.data.address?.addressLines || '',
        locality: data?.data.address?.locality || '',
        region: data?.data.address?.region || '',
        zipCode: data?.data.address?.zipCode || '',
        country: data?.data.address?.country || session?.gym?.country || CountryEnum.USA,
      },
      medicalNotes: data?.data.medicalNotes || '',
      emergencyName: data?.data.emergencyName || '',
      emergencyPhone: data?.data.emergencyPhone || '',
      permissions: data?.data.permissions || DEFAULTPERMISSIONS,
    },

    validate: {
      firstName: value => nameValidator(value),
      lastName: value => nameValidator(value),
      email: value => emailValidator(value),
      phone: value => (value ? phoneValidator(value) : null),
      emergencyName: value =>
        value && value.length > 128 ? 'Emergency name must be between 1-128 characters' : null,
      emergencyPhone: value => (value ? phoneValidator(value) : null),
    },

    transformValues: (values: DrawerStaffFormValues): DrawerStaffFormValues => ({
      ...values,
      phone: formatPhone(values.phone),
      emergencyPhone: formatPhone(values.emergencyPhone),
      address: {
        ...values.address,
        zipCode: values.address.zipCode?.toUpperCase() || null,
      },
    }),
  });

  const handleCountryChange = (value: string): void => {
    if (form.values.address.country != value) {
      form.setFieldValue('address.zipCode', '');
      form.setFieldValue('address.region', '');
      form.setFieldValue('address.country', value);
    }
  };

  const handlePermissionsChange = (permissions: StaffWrite['permissions']): void => {
    var permissionsToAdd: StaffWrite['permissions'] = [];
    const permissionToCheckOff: Partial<Record<Permission, Permission>> = {
      AthleteMutate: 'AthleteView',
      AthletePackageMutate: 'AthleteView',
      StaffMutate: 'StaffView',
      FlowMutate: 'FlowView',
      CheckInProgramManage: 'CheckInProgramView',
      PayrollMutate: 'PayrollReportViewAll',
    };
    (Object.entries(permissionToCheckOff) as [Permission, Permission][]).map(
      ([key, value]) =>
        permissions.includes(key) && !permissions.includes(value) && permissionsToAdd.push(value),
    );
    form.setFieldValue('permissions', permissions.concat([...new Set(permissionsToAdd)]));
  };

  const handleCloseAndReset = (): void => {
    closeHandler();
    form.reset();
    if (medicalOpened) handleMedicalToggle();
    if (permissionsOpened) handlePermissionsToggle();
  };

  const revealInvalidMedicalInfo = (errors: typeof form.errors): void => {
    if (errors.emergencyName || errors.emergencyPhone) openMedical();
  };

  const handleSubmit = (values: DrawerStaffFormValues): void => {
    const payload = {
      ...values,
      dob: values.dob ? dayjs(values.dob).format('YYYY-MM-DD') : null,
      phone: values.phone.length > 10 ? values.phone : '',
      gender: values.gender ?? null,
      isCoach: values.isCoach ?? false,
      emergencyName: values.emergencyName ?? null,
      emergencyPhone: values.emergencyPhone.length > 10 ? values.emergencyPhone : '',
      medicalNotes: values.medicalNotes ?? null,
    };

    if (type === 'create') {
      createMutation.mutate(
        {
          ...payload,
          staffId: uuidv4(),
        },
        {
          onError: error => {
            errorNotification('Staff creation failed', error.response?.data.message);
          },
          onSuccess: response => {
            successNotification('Staff creation success', `${response.data.fullName} successfully created.`);
            handleCloseAndReset();
          },
        },
      );
    }
    if (type === 'edit' && data?.data) {
      updateMutation.mutate(
        {
          ...payload,
          staffId: data?.data.staffId,
        },
        {
          onError: error => {
            errorNotification('Staff update failed', error.response?.data.message);
          },
          onSuccess: response => {
            successNotification('Staff update success', `${response.data.fullName} successfully updated.`);
            handleCloseAndReset();
          },
        },
      );
    }
  };

  useShallowEffect(() => {
    if (type === 'edit' && data?.data && isSuccess) {
      form.setValues({
        ...data.data,
        dob: data.data.dob ? dayjs(data.data.dob).toDate() : undefined,
        phone: data.data.phone ?? '',
        emergencyPhone: data.data.emergencyPhone ?? '',
      });
    }
  }, [data?.data, type]);

  // Reset form and regenerate email when drawer opens for demo gyms
  React.useEffect(() => {
    if (opened && isDemoGym && type === 'create') {
      form.setFieldValue('email', generateDemoGymFakeEmail());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opened, isDemoGym, type]);

  return (
    <Drawer
      opened={opened}
      size={'lg'}
      title={title}
      closeOnClickOutside
      footer={
        <Group position='right'>
          <Button variant='subtle' onClick={handleCloseAndReset}>
            Cancel
          </Button>
          <Button
            form={formId}
            type='submit'
            data-cy='saveStaffButton'
            loading={createMutation.isLoading || updateMutation.isLoading || isLoading}
            disabled={!permission}
          >
            Save
          </Button>
        </Group>
      }
      onClose={handleCloseAndReset}
    >
      <LoadingOverlay visible={type === 'edit' && (isLoading || isFetching)} />
      {type === 'edit' && (
        <Group mb='md' position='center'>
          <ImageUploader staffProp={data?.data} />
        </Group>
      )}
      <form id={formId} onSubmit={form.onSubmit(handleSubmit, revealInvalidMedicalInfo)}>
        <Stack spacing='xs'>
          <Group grow align='top'>
            <TextInput
              data-cy='staffFirstNameInput'
              label='First Name'
              placeholder='Enter First Name'
              required
              {...form.getInputProps('firstName')}
              disabled={!permission}
            />
            <TextInput
              data-cy='staffLastNameInput'
              label='Last Name'
              placeholder='Enter Last Name'
              required
              {...form.getInputProps('lastName')}
              disabled={!permission}
            />
          </Group>

          <Group>
            <TextInput
              data-cy='staffEmailInput'
              type='email'
              label='Email / Login'
              placeholder={isDemoGym && type === 'create' ? 'Auto-generated demo email' : 'Enter Email'}
              required
              disabled={
                (type === 'edit' && !canChangePassword) || (isDemoGym && type === 'edit') || isDemoGym
              }
              sx={{ flexGrow: 1 }}
              {...form.getInputProps('email')}
            />
            {type === 'edit' && !canChangePassword && (
              <Box pt='20px'>
                <HelpButton width={'md'} position='top-start' withinPortal>
                  <Text size='sm' sx={{ whiteSpace: 'normal' }}>
                    {`This user has activated their account using this email. To maintain login security, the email change must be initiated by the user.`}
                  </Text>
                </HelpButton>
              </Box>
            )}
          </Group>

          <Group grow align='top'>
            <Input.Wrapper error={form.getInputProps('phone').error}>
              <Input.Label>Phone Number</Input.Label>
              <PhoneInput
                defaultCountry={session?.gym?.country.toLowerCase()}
                countries={phoneInputCountries}
                value={form.values.phone || ''}
                onChange={form.setFieldValue.bind(null, 'phone')}
                placeholder='Enter Phone Number'
                disabled={!permission}
                inputStyle={{ width: '100%' }}
                data-cy='staffPhoneInput'
                style={{ position: 'relative', zIndex: 2 }}
              />
            </Input.Wrapper>
            <DateInput
              data-cy='staffDOBInput'
              label='Date of Birth'
              placeholder={dateFormat}
              valueFormat={dateFormat}
              clearable
              defaultLevel='decade'
              {...form.getInputProps('dob')}
              disabled={!permission}
            />
          </Group>

          <Group grow>
            <Select
              data-cy='staffGenderInput'
              label='Gender'
              placeholder='Select Gender'
              data={['Male', 'Female', 'Transgender', 'Non-binary/Non-conforming', 'Prefer not to respond']}
              {...form.getInputProps('gender')}
              disabled={!permission}
            />
            <Checkbox
              {...form.getInputProps('isCoach')}
              data-cy='coachSelectButton'
              label='Coach'
              checked={form.getInputProps('isCoach').value ?? false}
              style={{ paddingTop: 20, paddingLeft: 20 }}
              disabled={!permission}
            />
          </Group>

          <TextInput
            data-cy='staffAddressInput'
            label='Address'
            placeholder='Enter Address'
            {...form.getInputProps('address.addressLines')}
            disabled={!permission}
          />

          <Group grow align='top'>
            <TextInput
              data-cy='staffCityInput'
              label='City'
              placeholder='Enter City'
              {...form.getInputProps('address.locality')}
              disabled={!permission}
            />
            <RegionSelect
              country={form.values.address.country}
              disabled={!permission}
              clearable
              byCountryProps={{
                [CountryEnum.CANADA]: { 'data-cy': 'staffProvinceInput' },
                [CountryEnum.USA]: { 'data-cy': 'staffStateInput' },
              }}
              {...form.getInputProps('address.region')}
            />
          </Group>

          <Group grow align='top'>
            <PostalCodeInput
              country={form.values.address.country}
              {...form.getInputProps('address.zipCode')}
              disabled={!permission}
              data-cy='staffZipInput'
            />
            <CountrySelect
              {...form.getInputProps('address.country')}
              onChange={(value: string) => handleCountryChange(value)}
              required
              disabled={!permission}
            />
          </Group>

          <Divider mx='-xl' my='lg' />

          <ExpandableSection title='Medical Info' opened={medicalOpened} handleToggle={handleMedicalToggle}>
            <Stack spacing='xs'>
              <TextInput
                label='Emergency Contact'
                placeholder='Enter Full Name'
                {...form.getInputProps('emergencyName')}
                data-cy='staffEmergencyNameInput'
                disabled={!permission}
              />

              <Input.Wrapper error={form.getInputProps('emergencyPhone').error}>
                <Input.Label>Phone Number</Input.Label>
                <PhoneInput
                  data-cy='staffEmergencyPhoneInput'
                  defaultCountry={session?.gym?.country.toLowerCase()}
                  countries={phoneInputCountries}
                  value={form.values.emergencyPhone || ''}
                  onChange={form.setFieldValue.bind(null, 'emergencyPhone')}
                  placeholder='Enter Phone Number'
                  disabled={!permission}
                  style={{ position: 'relative', zIndex: 2 }}
                  inputStyle={{ width: '100%' }}
                />
              </Input.Wrapper>
              <Textarea
                data-cy='staffMedicalNotesInput'
                label='Medical Notes'
                placeholder='Enter Medical Notes'
                {...form.getInputProps('notes')}
                disabled={!permission}
              />
            </Stack>
          </ExpandableSection>

          <Divider mx='-xl' my='lg' />

          <ExpandableSection
            title='Permissions'
            opened={permissionsOpened}
            handleToggle={handlePermissionsToggle}
          >
            <Checkbox.Group {...form.getInputProps('permissions')} onChange={handlePermissionsChange}>
              <Group grow align='top'>
                <Stack>
                  <InfoCard
                    header={<Title order={4}>Dashboard Metrics and Reports</Title>}
                    actionButton={<></>}
                    content={
                      <InfoCardSection
                        content={
                          <PermissionContent
                            permissions={[
                              { title: 'View Business Metrics', permission: 'BusinessMetricsReportsView' },
                              { title: 'View Accounting Reports', permission: 'AccountingReportsView' },
                              { title: 'View Payment Reports', permission: 'PaymentReportsView' },
                              { title: 'View Athlete Reports', permission: 'AthleteReportsView' },
                              {
                                title: 'View Package Reports',
                                permission: 'PackageReportsView',
                              },
                              {
                                title: 'View Utilization Reports',
                                permission: 'UtilizationReportsView',
                              },
                              {
                                title: 'View Reservations Report',
                                permission: 'ReservationsReportView',
                              },
                            ]}
                            disabled={!permission}
                          />
                        }
                      />
                    }
                  />
                  <InfoCard
                    header={<Title order={4}>Calendar and Events</Title>}
                    actionButton={<></>}
                    content={
                      <InfoCardSection
                        content={
                          <PermissionContent
                            permissions={[
                              {
                                title: 'Create/Edit/Delete Event Types and Locations',
                                permission: 'EventTypeLocationMutate',
                              },
                              {
                                title: 'Create/Edit/Delete Events and Attendance',
                                permission: 'EventMutate',
                              },
                              ...(workoutTrackerEnabled
                                ? []
                                : [
                                    {
                                      title: 'Create/Edit/Delete Programming',
                                      permission: 'ProgramMutate',
                                    },
                                  ]),
                              {
                                title: 'Use Kiosk',
                                permission: 'Kiosk',
                              },
                            ]}
                            disabled={!permission}
                          />
                        }
                      />
                    }
                  />
                  <InfoCard
                    header={<Title order={4}>Products</Title>}
                    actionButton={<></>}
                    content={
                      <InfoCardSection
                        content={
                          <PermissionContent
                            permissions={[
                              {
                                title: 'Create/Edit/Delete Package Templates',
                                permission: 'PackageMutate',
                              },
                              {
                                title: 'Create/Edit/Delete Retail, Fees, and Miscellaneous Items',
                                permission: 'ProductMutate',
                              },
                              {
                                title: 'Create/Edit/Archive Discount Codes',
                                permission: 'DiscountMutate',
                              },
                              {
                                title: 'Create/Edit/Delete Tax Rates',
                                permission: 'TaxRateMutate',
                              },
                              {
                                title: 'Manage Sales Portal',
                                permission: 'SalesPortalMutate',
                              },
                            ]}
                            disabled={!permission}
                          />
                        }
                      />
                    }
                  />
                  <CheckInProgramPermissionSection
                    checkedPermissions={form.values.permissions}
                    disabled={!permission}
                  />
                  <InfoCard
                    header={<Title order={4}>Users</Title>}
                    actionButton={<></>}
                    content={
                      <InfoCardSection
                        content={
                          <PermissionContent
                            permissions={[
                              {
                                title: 'View Athletes',
                                permission: 'AthleteView',
                                defaultCheckedOffBy: ['AthleteMutate', 'AthletePackageMutate'],
                              },
                              {
                                title: 'Create/Edit/Delete Athletes',
                                permission: 'AthleteMutate',
                              },
                              {
                                title: 'Manage Athlete Packages',
                                permission: 'AthletePackageMutate',
                              },
                              {
                                title: 'View Staff',
                                permission: 'StaffView',
                                defaultCheckedOffBy: ['StaffMutate'],
                              },
                              {
                                title: 'Create/Edit/Delete Staff',
                                permission: 'StaffMutate',
                              },
                            ]}
                            checkedPermissions={form.values.permissions}
                            disabled={!permission}
                          />
                        }
                      />
                    }
                  />
                  <InfoCard
                    header={<Title order={4}>Documents</Title>}
                    actionButton={<></>}
                    content={
                      <InfoCardSection
                        content={
                          <PermissionContent
                            permissions={[
                              {
                                title: 'Create/Edit/Delete Document Templates',
                                permission: 'DocumentTemplateMutate',
                              },
                              { title: 'Manage Issued Documents', permission: 'IssuedDocumentMutate' },
                            ]}
                            disabled={!permission}
                          />
                        }
                      />
                    }
                  />
                  <InfoCard
                    header={<Title order={4}>Invoices</Title>}
                    actionButton={<></>}
                    content={
                      <InfoCardSection
                        content={
                          <PermissionContent
                            permissions={[
                              {
                                title: 'View Athlete Invoices',
                                permission: 'InvoiceAthleteView',
                              },
                              { title: 'View Invoices Page', permission: 'InvoiceReportsView' },
                              { title: 'Manage Invoices', permission: 'InvoiceMutate' },
                              {
                                title: 'Make Charges for Retail, Fees, and Miscellaneous',
                                permission: 'InvoiceCreate',
                              },
                            ]}
                            disabled={!permission}
                          />
                        }
                      />
                    }
                  />
                  <InfoCard
                    header={<Title order={4}>Flows</Title>}
                    actionButton={<></>}
                    content={
                      <InfoCardSection
                        content={
                          <PermissionContent
                            permissions={[
                              {
                                title: 'View User Alerts',
                                permission: 'FlowView',
                                defaultCheckedOffBy: ['FlowMutate'],
                              },
                              {
                                title: 'Create/Edit User Alerts',
                                permission: 'FlowMutate',
                              },
                            ]}
                            checkedPermissions={form.values.permissions}
                            disabled={!permission}
                          />
                        }
                      />
                    }
                  />
                  <InfoCard
                    header={<Title order={4}>Payroll</Title>}
                    actionButton={<></>}
                    content={
                      <InfoCardSection
                        content={
                          <PermissionContent
                            permissions={[
                              {
                                title: 'View individual payroll report',
                                permission: 'PayrollReportViewOwn',
                                defaultCheckedOffBy: ['PayrollMutate', 'PayrollReportViewAll'],
                              },
                              {
                                title: 'View all staff payroll reports',
                                permission: 'PayrollReportViewAll',
                                defaultCheckedOffBy: ['PayrollMutate'],
                              },
                              {
                                title: 'Manage Payroll',
                                permission: 'PayrollMutate',
                              },
                            ]}
                            checkedPermissions={form.values.permissions}
                            disabled={!permission}
                          />
                        }
                      />
                    }
                  />
                  <InfoCard
                    header={<Title order={4}>Stripe</Title>}
                    actionButton={<></>}
                    content={
                      <InfoCardSection
                        content={
                          <PermissionContent
                            permissions={[
                              {
                                title: 'Access Stripe Dashboard',
                                permission: 'StripeDashboardView',
                              },
                            ]}
                            disabled={!permission}
                          />
                        }
                      />
                    }
                  />
                  {workoutTrackerEnabled && (
                    <InfoCard
                      header={<Title order={4}>Workouts</Title>}
                      actionButton={<></>}
                      content={
                        <InfoCardSection
                          content={
                            <PermissionContent
                              permissions={[
                                {
                                  title: 'Create/Edit/Delete Workouts, Templates and Movements',
                                  permission: 'WorkoutMutate',
                                },
                              ]}
                              checkedPermissions={form.values.permissions}
                              disabled={!permission}
                            />
                          }
                        />
                      }
                    />
                  )}
                  <InfoCard
                    header={<Title order={4}>Gym Settings</Title>}
                    actionButton={<></>}
                    content={
                      <InfoCardSection
                        content={
                          <PermissionContent
                            permissions={[{ title: 'Manage Gym Settings', permission: 'GymMutate' }]}
                            disabled={!permission}
                          />
                        }
                      />
                    }
                  />
                </Stack>
              </Group>
            </Checkbox.Group>
          </ExpandableSection>
        </Stack>
      </form>
    </Drawer>
  );
};

export default DrawerStaffForm;
